<script setup>
import { storeToRefs } from 'pinia'
import { onMounted } from 'vue'
import { trackAnalyticsInvestmentProcessStep2 } from '../../../utilities/useTracking'
import { isLegalProfile, isNaturalProfile } from '../../../utilities/investment-process/useUserProfileType'
import { notify } from '../../../utilities/useNotifications'
import { i18n } from '../../../localization'
import {
    editCompanyData,
    editPersonalData,
    navigateToNextStep,
} from '../../../utilities/account/useInvestmentProcessNavigation'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import AccountPanelPersonalMissingDataAlert from '../../account/personal/AccountPanelPersonalMissingDataAlert.vue'
import AccountPanelLegalMissingDataAlert from '../../account/legal/AccountPanelLegalMissingDataAlert.vue'
import AccountPanelLegalMissingVerificationAlert from '../../account/legal/AccountPanelLegalMissingVerificationAlert.vue'
import AccountPersonalOverviewPersonalData from '../../account/personal/overview/AccountPersonalOverviewPersonalData.vue'
import AccountLegalOverviewCompanyData from '../../account/legal/overview/AccountLegalOverviewCompanyData.vue'
import InvestmentProcessNaviationButtons from '../InvestmentProcessNaviationButtons.vue'
import VermAnlGInvestmentProcessConfirmSection from './VermAnlGInvestmentProcessConfirmSection.vue'
import AccountPanelPersonalMissingVerificationAlert from '../../account/personal/AccountPanelPersonalMissingVerificationAlert.vue'
import { getAccountLink } from '../../../utilities/useLinks'

onMounted(() => {
    trackAnalyticsInvestmentProcessStep2()
})

const personalStore = usePersonalAccountDataState()
const legalStore = useLegalAccountDataState()

const {
    vibCurrentLocation,
    vibDate,
    vibEmail,
    vibFirstName,
    vibLastName,
    vibPlaceOfBirth,
    vibDateOfBirth,
    vibIdCardPassportNumber,
    issuingAuthority,
    vibAddress,
    vibPostalCode,
    vibCity,
    vibState,
    vibInvestmentInformation,
    vibInvestmentUnder1000,
    vibInvestmentFinancialAssetsOver10000,
    vibInvestmentIncome,
    vibInvestmentLimit,
    vibError,
} = storeToRefs(useInvestmentProcessDataState())

async function checkLegalRequirements() {
    vibError.value = null
    // Check required fields that don't need to match personal store values
    if (
        !vibCurrentLocation.value ||
        !vibDate.value ||
        !vibPlaceOfBirth.value ||
        !vibDateOfBirth.value ||
        !vibIdCardPassportNumber.value ||
        !issuingAuthority.value ||
        !vibAddress.value ||
        !vibPostalCode.value ||
        !vibCity.value ||
        !vibState.value ||
        !vibInvestmentInformation.value ||
        (!vibInvestmentUnder1000.value && !vibInvestmentFinancialAssetsOver10000.value && !vibInvestmentIncome.value) ||
        !vibInvestmentLimit.value
    ) {
        notify('Error', i18n.global.t('investment_process.please_fill_required_fields'))
        return
    }

    // Check fields that must match personal store values
    if (vibEmail.value !== personalStore.naturalProfile.email) {
        vibError.value = 'vib_email'
        window.scrollTo({ top: document.getElementById('vib_email').offsetTop - 100, behavior: 'smooth' })
        notify('Error', i18n.global.t('investment_process.email_must_match_profile'))
        return
    }

    if (vibFirstName.value !== personalStore.naturalProfile.first_name) {
        vibError.value = 'vib_first_name'
        window.scrollTo({ top: document.getElementById('vib_first_name').offsetTop - 100, behavior: 'smooth' })
        notify('Error', i18n.global.t('investment_process.first_name_must_match_profile'))
        return
    }

    if (vibLastName.value !== personalStore.naturalProfile.last_name) {
        vibError.value = 'vib_last_name'
        window.scrollTo({ top: document.getElementById('vib_last_name').offsetTop - 100, behavior: 'smooth' })
        notify('Error', i18n.global.t('investment_process.last_name_must_match_profile'))
        return
    }

    // Check if profile has all required attributes
    if (
        (isNaturalProfile() && !personalStore.hasAllRequiredNaturalProfileAttributesForInvestment) ||
        (isLegalProfile() && !legalStore.hasAllRequiredProfileAttributesForInvestment)
    ) {
        notify('Error', i18n.global.t('verification.complete_data_description'))
        return
    }

    // Check verification status
    if (
        (isNaturalProfile() && personalStore.isVerified === false) ||
        (isLegalProfile() && legalStore.isVerified === false)
    ) {
        notify('Error', i18n.global.t('verification.complete_verification_outstanding'))
        return
    }

    // All validations passed
    navigateToNextStep()
}

function goToVerificationPage() {
    window.location.href = getAccountLink() + '?menu=Verification'
}
</script>

<template>
    <div class="space-y-6 px-6 py-6">
        <AccountPanelPersonalMissingDataAlert
            v-if="isNaturalProfile() && !personalStore.hasAllRequiredNaturalProfileAttributesForInvestment"
            class="shadow-none" />

        <AccountPanelPersonalMissingVerificationAlert
            v-if="isNaturalProfile() && !personalStore.isVerified"
            @button-click="goToVerificationPage()" />

        <AccountPanelLegalMissingDataAlert
            v-if="isLegalProfile() && !legalStore.hasAllRequiredProfileAttributesForInvestment"
            class="shadow-none" />
        <AccountPanelLegalMissingVerificationAlert
            v-if="isLegalProfile() && !legalStore.isVerified"
            @button-click="goToVerificationPage()" />
        <h3 class="pb-3 text-base font-bold text-gray-700">{{ $t('account_settings.overview') }}</h3>

        <div
            v-if="isNaturalProfile()"
            class="overflow-hidden border border-gray-200 bg-white sm:col-span-1 sm:rounded-md">
            <AccountPersonalOverviewPersonalData @edit="editPersonalData" />
        </div>
        <div
            v-else-if="isLegalProfile()"
            class="overflow-hidden border border-gray-200 bg-white sm:col-span-1 sm:rounded-md">
            <AccountLegalOverviewCompanyData @edit="editCompanyData" />
        </div>
        <VermAnlGInvestmentProcessConfirmSection />
        <InvestmentProcessNaviationButtons :on-next-click="checkLegalRequirements" />
    </div>
</template>

<style scoped></style>
